/**
 * `/itineraries/:slug` — read-only itinerary view. Loads via
 * `trpc.itinerary.getBySlug` (server applies the D4a recompute-on-view legs +
 * view flags) and renders the read-only view. A non-viewable target resolves
 * to a stub, never a raw 404 (D3).
 */

import { useParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Heading1, BodyText } from '../components/ui/Typography';
import { ItineraryView } from '../components/itinerary/ItineraryView';
import { useCloneIntentAutoRetry } from '../components/itinerary/useCloneIntent';

export const ItineraryViewPage = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  // Post-auth handoff: if the user just signed in after clicking
  // "Sign in to save", a fresh clone intent fires the clone here on mount
  // (no re-click) and routes to the new clone's /edit (U7 DQ-1).
  useCloneIntentAutoRetry();
  const query = trpc.itinerary.getBySlug.useQuery(
    { slug },
    { enabled: slug.length > 0 }
  );

  if (query.isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer className="mt-2">
          <BodyText className="text-gray-500">Loading itinerary…</BodyText>
        </PageContainer>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer className="mt-2">
          <Heading1>Itinerary unavailable</Heading1>
          <BodyText className="mt-2 text-gray-600">
            This itinerary is no longer available.
          </BodyText>
        </PageContainer>
      </div>
    );
  }

  return <ItineraryView itinerary={query.data} />;
};
